import React from "react";
import { getResultLabel, getResultColor, fmtHM, fmtDate, fmtDur } from "./helpers";

/**
 * EventCard — tarjeta de evento abierto/cerrado.
 *
 * Props:
 *   alarm: Firestore alarm document (with extra fields: dispatchedBy, dispatchTime, arrivedTime, resolvedAt, etc.)
 *   onClick: (alarm) => void     — open dispatch or close modal
 *   onArrive: (alarmId) => void  — mark guard arrived (optional, only for dispatched alarms)
 *   compact: bool                — compact mode for closed events list
 */

function getPhase(alarm) {
  if (alarm.resolvedAt)             return "closed";
  if (!alarm.dispatchedBy) {
    // Is it still "active" (recent) or "unmanaged" (old/ceased)?
    const ageMs = Date.now() - (alarm.createdAt?.seconds * 1000 || 0);
    return ageMs < 900000 ? "urgent" : "unmanaged"; // 15min threshold
  }
  if (alarm.arrivedTime)            return "arrived";
  return "dispatched";
}

const PHASE_META = {
  urgent:     { bg: "#dc2626", tx: "#fff", lb: "SIN DESPACHAR", pulse: true,  action: "⚡ Despachar", actionBg: "#dc2626" },
  unmanaged:  { bg: "#f59e0b", tx: "#000", lb: "SIN DESPACHAR", pulse: true,  action: "Despachar",    actionBg: "#f59e0b" },
  dispatched: { bg: "#3b82f6", tx: "#fff", lb: "DESPACHADO",    pulse: true,  action: "Cerrar",       actionBg: "#3b82f6" },
  arrived:    { bg: "#0d9488", tx: "#fff", lb: "EN ZONA",       pulse: false, action: "Cerrar",       actionBg: "#0d9488" },
  closed:     { bg: "#1f2937", tx: "#64748b", lb: "CERRADO",    pulse: false, action: null,           actionBg: null },
};

const BORDER_COLOR = {
  urgent:     "#dc2626",
  unmanaged:  "#f59e0b60",
  dispatched: "#3b82f630",
  arrived:    "#0d948830",
  closed:     "var(--border)",
};

/** Badge pill */
function PhaseBadge({ phase, alarm }) {
  const m = PHASE_META[phase];
  const lb = phase === "closed" ? (getResultLabel(alarm) || "CERRADO") : m.lb;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 7px", borderRadius: 4,
      background: phase === "closed" ? `${getResultColor(alarm)}20` : m.bg,
      color: phase === "closed" ? getResultColor(alarm) : m.tx,
      fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
      fontFamily: "var(--mono)", whiteSpace: "nowrap",
    }}>
      {m.pulse && <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.tx, animation: "pulse 1.5s infinite" }} />}
      {lb}
    </span>
  );
}

export default function EventCard({ alarm, onClick, onArrive, compact }) {
  const phase = getPhase(alarm);
  const m     = PHASE_META[phase];
  const startMs = alarm.createdAt?.seconds ? alarm.createdAt.seconds * 1000 : null;
  const updatedMs = alarm.updatedAt?.seconds ? alarm.updatedAt.seconds * 1000 : null;

  // RT State Logic
  const isActive = updatedMs && (Date.now() - updatedMs) < 30000;
  let rtState = "detenido";
  let rtColor = "#6b7280"; // Gray
  let rtLabel = "Ataque detenido";
  let rtPulse = false;

  if (isActive) {
    const elapsed = startMs ? (updatedMs - startMs) : 0;
    const durMs = (alarm.duration || 0) * 1000;
    if (elapsed > durMs + 30000) {
      rtState = "intermitente";
      rtColor = "#f97316"; // Orange
      rtLabel = "Ataque intermitente";
      rtPulse = true;
    } else {
      rtState = "activo";
      rtColor = "#dc2626"; // Red
      rtLabel = "Ataque activo";
      rtPulse = true;
    }
  }

  // Auto-close warning
  const isWarning = !alarm.resolvedAt && startMs && (Date.now() - startMs > 7.5 * 3600000);

  /* ── Compact mode (closed events list) ── */
  if (compact) {
    const hasActiveAttack = isActive && (rtState === "activo" || rtState === "intermitente");
    return (
      <div
        onClick={() => onClick?.(alarm)}
        style={{
          padding: "8px 10px",
          background: "var(--card)",
          borderRadius: 8,
          border: hasActiveAttack
            ? (rtState === "activo" ? "1px solid #dc262660" : "1px solid #f9731660")
            : "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          opacity: hasActiveAttack ? 1.0 : 0.8,
          cursor: "pointer",
          boxShadow: hasActiveAttack
            ? (rtState === "activo" ? "0 0 10px #dc262615" : "0 0 10px #f9731615")
            : "none",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--subtle)", whiteSpace: "nowrap" }}>
              {alarm.zoneName}
            </span>
            <PhaseBadge phase={phase} alarm={alarm} />
            {hasActiveAttack && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 6px",
                borderRadius: 4,
                background: rtColor,
                color: rtState === "intermitente" ? "#000" : "#fff",
                fontSize: 8,
                fontWeight: 800,
                letterSpacing: "0.02em",
                fontFamily: "var(--mono)",
                whiteSpace: "nowrap",
                boxShadow: "0 0 8px " + (rtState === "activo" ? "#dc262680" : "#f9731680"),
              }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: rtState === "intermitente" ? "#000" : "#fff", animation: "pulse 1.5s infinite" }} />
                <span>{rtLabel.toUpperCase()}</span>
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, fontSize: 9, color: "var(--dim)", fontFamily: "var(--mono)" }}>
            {startMs && <span>{fmtDate(startMs)} {fmtHM(startMs)}</span>}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>ID: {alarm.docID}</span>
          </div>
        </div>
        <span style={{ fontSize: 12, opacity: 0.5, flexShrink: 0 }}>✏️</span>
      </div>
    );
  }

  /* ── Full card (open events) ── */
  const isUrgent = phase === "urgent";

  return (
    <div style={{
      background: isUrgent ? "#dc262608" : "var(--card)",
      border: `2px solid ${BORDER_COLOR[phase] || "var(--border)"}`,
      borderRadius: 10,
      animation: isUrgent ? "urgentPulse 2s infinite" : "none",
      position: "relative", overflow: "hidden",
    }}>
      {/* Left accent stripe */}
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: m.actionBg || "#374151" }} />

      {/* Embedded status & duration badge in the top-right corner */}
      <div style={{
        position: "absolute",
        top: 0,
        right: 0,
        padding: "4px 10px",
        background: rtColor,
        color: rtState === "intermitente" ? "#000" : "#fff",
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: "0.04em",
        fontFamily: "var(--mono)",
        borderRadius: "0 0 0 8px", // top-right is rounded by card overflow:hidden
        display: "flex",
        alignItems: "center",
        gap: 6,
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          {rtPulse && <span style={{ width: 4, height: 4, borderRadius: "50%", background: rtState === "intermitente" ? "#000" : "#fff", animation: "pulse 1.5s infinite" }} />}
          <span>{rtLabel.toUpperCase()}</span>
        </div>
        {alarm.duration != null && (
          <>
            <span style={{ opacity: 0.5 }}>|</span>
            <span>⏱ {fmtDur(alarm.duration)}{rtState === "activo" && " ↑"}</span>
          </>
        )}
      </div>

      {/* Main tap area */}
      <button
        type="button"
        onClick={() => onClick?.(alarm)}
        style={{ width: "100%", textAlign: "left", padding: "11px 12px", cursor: "pointer", background: "none", border: "none", color: "var(--text)" }}
      >
        <div style={{ display: "flex", alignItems: "stretch", justifyContent: "space-between", gap: 12 }}>
          {/* Left Column (Main information) */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap", paddingRight: 180 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--bright)" }}>{alarm.zoneName}</span>
              {phase === "closed" && <PhaseBadge phase={phase} alarm={alarm} />}
            </div>
            <div style={{ display: "flex", gap: 10, fontSize: 12, color: "var(--muted)", flexWrap: "wrap", alignItems: "center" }}>
              {startMs && <span>🕐 {fmtDate(startMs)} {fmtHM(startMs)}</span>}
              {isWarning && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b" }}>⚠️ Cierre inminente</span>
              )}
              {alarm.dispatchedBy && <span style={{ color: "#60a5fa" }}>👤 {alarm.dispatchedBy}</span>}
            </div>
            {alarm.docID && (
              <div style={{ marginTop: 3, fontSize: 10, color: "var(--dim)", fontFamily: "var(--mono)" }}>ID: {alarm.docID}</div>
            )}
          </div>

          {/* Right Column (Action button at bottom) */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-end", gap: 8, flexShrink: 0 }}>
            {/* Bottom Right: Action button */}
            {m.action ? (
              <div style={{
                padding: "9px 14px", background: m.actionBg, borderRadius: 7,
                color: "#fff", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
              }}>
                {m.action}
              </div>
            ) : (
              <div style={{ height: 1 }} />
            )}
          </div>
        </div>
      </button>

      {/* Optional "guard arrived" strip — only for dispatched events */}
      {phase === "dispatched" && onArrive && (
        <button
          type="button"
          onClick={ev => { ev.stopPropagation(); onArrive(alarm.docID); }}
          style={{
            width: "100%", padding: "7px 12px",
            background: "#0d948815",
            borderTop: "1px solid #0d948830",
            border: "none", borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
            color: "#5eead4", fontSize: 10, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}
        >
          📍 Marcar: Guardia llegó a zona{" "}
          <span style={{ fontSize: 9, color: "#0d9488", fontFamily: "var(--mono)" }}>(opcional)</span>
        </button>
      )}
    </div>
  );
}
